import { useWeb3ModalProvider } from '@web3modal/ethers5/react';
import { BaseModal, Button, Divider, Flex, Input, Loader } from '@/lib';
import styles from './TransferModal.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { useState } from 'react';
import { safeTransferFrom } from '@/services/NFT/transfer';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';
import { useUpdateUserNFTs } from '@/hooks/useUpdateUserNFTs';

const ModalStatus = {
  Default: 'default',
  Pending: 'pending',
  Success: 'success',
  Fail: 'fail',
};

export const TransferModal = ({ onClose, nftData }) => {
  const { walletProvider } = useWeb3ModalProvider();
  const [status, setStatus] = useState(ModalStatus.Default);
  const { isTablet } = useAdaptiveWidth();
  const { collection_address, token_id, name, owner_address } = nftData;
  const { getNFTs } = useUpdateUserNFTs();

  const methods = useForm({
    defaultValues: {
      address: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = async (data) => {
    setStatus(ModalStatus.Pending);

    try {
      const response = await safeTransferFrom({
        ownerAddress: owner_address,
        receiverAddress: data.address,
        nftId: token_id,
        collectionAddress: collection_address,
        walletProvider,
      });

      if (response) {
        setStatus(ModalStatus.Success);
        getNFTs();
      } else {
        throw new Error();
      }
    } catch (error) {
      setStatus(ModalStatus.Fail);
    }
  };

  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      title={status === ModalStatus.Default ? 'Transfer your items' : ''}
      noCloseButton
      width="480px"
    >
      <FormProvider {...methods}>
        {status === ModalStatus.Default && (
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Flex vertical gap={24}>
              <div className={styles.subtitle}>
                You can transfer items from your address to another
              </div>
              <Flex vertical gap={10}>
                <span>Quantity</span>
                <Flex gap={10} align="center">
                  <img src="/svg/ftn.svg" alt="ftn" />
                  <span>1 item</span>
                </Flex>
              </Flex>
              <Divider noMargin />
              <Input
                label="Bahamut address"
                placeholder="Receiver address"
                required
                name="address"
              />
              <p className={styles.disclaimer}>
                Note: You will need to approve this transfer in your wallet to
                complete the process
              </p>
              <Flex gap={12}>
                <Button
                  color={isTablet ? 'black' : 'graphite'}
                  fullWidth
                  onClick={onClose}
                  type="button"
                >
                  Re-select
                </Button>
                <Button color="white" fullWidth type="submit">
                  Confirm
                </Button>
              </Flex>
            </Flex>
          </form>
        )}

        {status === ModalStatus.Pending && (
          <Flex vertical gap={24}>
            <Loader big className={styles.loader} />
            <Button disabled color="orange" noShadow>
              Transfer Underway…
            </Button>
          </Flex>
        )}

        {status === ModalStatus.Success && (
          <Flex vertical gap={24} align="center">
            <img
              src="/svg/success.svg"
              alt="success"
              className={styles.statusImage}
            />
            <Flex gap={12} vertical>
              <span className={styles.statusTitle}>Success</span>
              <p className={styles.statusDesc}>
                You have sent <span className={styles.highlight}>{name}</span>{' '}
                to the address
                <br />
                <span className={styles.highlight}>
                  {methods.getValues('address')}
                </span>
              </p>
            </Flex>
            <Flex gap={12} vertical>
              <Button color="white" fullWidth type="button" onClick={onClose}>
                Continue
              </Button>
            </Flex>
          </Flex>
        )}

        {status === ModalStatus.Fail && (
          <Flex vertical gap={24} align="center">
            <img
              src="/svg/failed.svg"
              alt="fail"
              className={styles.statusImage}
            />
            <Flex gap={12} vertical>
              <span className={styles.statusTitle}>Transfer Failed</span>
              <p className={styles.statusDesc}>
                Unfortunately, the transfer of{' '}
                <span className={styles.highlight}>{name}</span> to the address{' '}
                <span className={styles.highlight}>
                  {methods.getValues('address')}
                </span>{' '}
                could not be completed
              </p>
            </Flex>
            <Flex gap={12} vertical>
              <Button
                color="white"
                fullWidth
                type="button"
                onClick={() => setStatus(ModalStatus.Default)}
              >
                Retry Transfer
              </Button>
              <Button
                color={isTablet ? 'black' : 'graphite'}
                fullWidth
                onClick={onClose}
                type="button"
              >
                Send Later
              </Button>
            </Flex>
          </Flex>
        )}
      </FormProvider>
    </BaseModal>
  );
};
